import {
    useState
} from "react";
import {
    ComponentHeader,
    ExampleSection,
    getSectionToc
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    Button,
    Badge
} from "ncore-ui-kit";
import {
    InlineCode,
    DocsTable,
    Paragraph,
    CodeBlock,
    Callout,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    BellIcon
} from "../../anatomy/icons";

const NAME = "NotificationIndicator";

const USAGE_CODE = `<Badge.Indicator
    type="danger"
    count={3}
>
    <Button
        accessibilityLabel="Notifications, 3 unread"
        onPress={handleOpenNotifications}
        variant="tonal"
        icon={BellIcon}
        type="neutral"
    />
</Badge.Indicator>`;

const INITIAL_COUNT = 3;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    }
], NAME, t);

const NotificationIndicatorPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        count,
        setCount
    ] = useState(INITIAL_COUNT);

    const handleAdd = () => setCount((current) => current + 1);

    const handleReadAll = () => setCount(0);

    return <Article>
        <ComponentHeader
            lead={t("notificationIndicatorPage.lead")}
            name={NAME}
        />
        <Callout
            title={t("notificationIndicatorPage.noticeTitle")}
        >
            {t("notificationIndicatorPage.noticeText")}
        </Callout>
        <DocsTable
            rows={[
                [
                    <InlineCode>
                        {"title={number}"}
                    </InlineCode>,
                    <InlineCode>
                        {"count={number}"}
                    </InlineCode>
                ],
                [
                    <InlineCode>
                        {"title omitted (dot)"}
                    </InlineCode>,
                    <InlineCode>
                        {"count omitted (dot)"}
                    </InlineCode>
                ],
                [
                    <InlineCode>
                        {"location.left, location.right"}
                    </InlineCode>,
                    <InlineCode>
                        {"location.start, location.end"}
                    </InlineCode>
                ],
                [
                    <InlineCode>
                        {"max 99 / 99+"}
                    </InlineCode>,
                    <InlineCode>
                        {"max={99}"}
                    </InlineCode>
                ],
                [
                    <InlineCode>
                        {"children, type, location, isVisible"}
                    </InlineCode>,
                    t("notificationIndicatorPage.mapSame")
                ],
                [
                    <InlineCode>
                        {"isLoading, isDisabled, onPress"}
                    </InlineCode>,
                    t("notificationIndicatorPage.mapSame")
                ],
                [
                    <InlineCode>
                        {"floorBackgroundColor, spreadBehaviour"}
                    </InlineCode>,
                    t("notificationIndicatorPage.mapSame")
                ],
                [
                    <InlineCode>
                        {"titleStyle, titleVariant, customLocalize"}
                    </InlineCode>,
                    t("notificationIndicatorPage.mapSame")
                ],
                [
                    <InlineCode>
                        {"style, customTheme, accessibilityLabel, aria-*"}
                    </InlineCode>,
                    t("notificationIndicatorPage.mapSame")
                ]
            ]}
            headers={[
                t("notificationIndicatorPage.mapOld"),
                t("notificationIndicatorPage.mapNew")
            ]}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("notificationIndicatorPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("notificationIndicatorPage.overviewCaption")}
            >
                <Badge.Indicator
                    floorBackgroundColor="mid"
                    spreadBehaviour="free"
                    type="danger"
                    count={count}
                >
                    <Button
                        accessibilityLabel={t("notificationIndicatorPage.label")}
                        onPress={handleReadAll}
                        variant="tonal"
                        icon={BellIcon}
                        type="neutral"
                    />
                </Badge.Indicator>
                <Button
                    title={t("notificationIndicatorPage.add")}
                    spreadBehaviour="free"
                    onPress={handleAdd}
                    variant="outline"
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default NotificationIndicatorPage;
